import { Typography, Chip } from '@mui/material';
import React, { MouseEvent } from 'react';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Popover, Stack } from '@mui/material';
import { useState } from 'react';
import { featureFlagMap } from './constants';
import { useStoreActions } from 'src/stores';
import { PopUpEnum } from 'src/stores/NotificationStore';
import { useTranslation } from 'react-i18next';
import { CompanyFeatureFlagProps } from './FeatureFlagSwitch.types';
import CancelIcon from '@mui/icons-material/Cancel';
import { useToggleFeatureFlag } from 'src/api/hooks/useFeatureFlag';
import { FeatureFlagKey } from 'src/DTO/Company.type';

export const FeatureFlagSwitch: React.FC<CompanyFeatureFlagProps> = params => {
  const { t } = useTranslation();
  const setPopUpData = useStoreActions(action => action.popUp.setPopUp);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [featureChecked, setFeatureChecked] = useState<FeatureFlagKey[]>(
    params.row?.featureFlags || [],
  );

  const availableFeatureFlags: FeatureFlagKey[] = params.availableFeatureFlags || [];
  const enabledFeatureCount = featureChecked.length;
  const totalFeatureCount = availableFeatureFlags.length;

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget as HTMLButtonElement);
  };

  const [toggleFeatureFlag] = useToggleFeatureFlag({
    onError: () => {
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.error,
        message: t('alerts.toggleFeatureFlag.error'),
      });
    },
  });

  const handleToggleFlag = (flagKey: FeatureFlagKey, checked: boolean) => {
    setFeatureChecked(prev => {
      if (checked) {
        return [...prev, flagKey];
      } else {
        return prev.filter(flag => flag !== flagKey);
      }
    });

    toggleFeatureFlag({
      variables: {
        companyId: params.row.id,
        featureKey: flagKey,
      },
    });
  };

  return (
    <Box>
      <Chip
        icon={<SettingsIcon sx={{ fontSize: 16 }} />}
        label={`${enabledFeatureCount}/${totalFeatureCount}`}
        onClick={handleClick}
        size="medium"
        variant="outlined"
        color={enabledFeatureCount > 0 ? 'primary' : 'default'}
        sx={{
          width: 75,
          cursor: 'pointer',
          fontSize: '0.9rem',
          '&:hover': {
            backgroundColor: 'action.hover',
            boxShadow: 1,
          },
        }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        disableRestoreFocus
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 0.5,
            p: 1.3,
            borderRadius: 5,
            border: '1px solid',
            borderColor: 'primary.main',
            boxShadow: 10,
            overflow: 'auto',
            animation: 'slideDown 0.25s ease-out',
            '@keyframes slideDown': {
              from: {
                opacity: 0,
                transform: 'translateY(-10px)',
              },
              to: {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          },
        }}>
        <Stack spacing={1.25}>
          {availableFeatureFlags.map(value => {
            const isEnabled = featureChecked.includes(value);
            const flagName = featureFlagMap[value] || value;

            return (
              <Box
                key={value}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  py: 1.25,
                  px: 2,
                  borderRadius: 4,
                  border: '1px solid',
                  borderColor: isEnabled ? 'primary.main' : 'divider',
                  backgroundColor: isEnabled ? 'action.selected' : 'transparent',
                  transition: 'all 0.2s ease-in-out',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: isEnabled ? 'action.selected' : 'action.hover',
                    transform: 'scale(1.02)',
                    boxShadow: 1,
                  },
                  '&:active': {
                    transform: 'scale(0.98)',
                  },
                }}
                onClick={() => handleToggleFlag(value, !isEnabled)}>
                {isEnabled ? (
                  <CheckCircleIcon
                    sx={{
                      fontSize: 20,
                      color: 'primary.main',
                    }}
                  />
                ) : (
                  <CancelIcon
                    sx={{
                      fontSize: 20,
                      color: 'text.disabled',
                    }}
                  />
                )}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isEnabled ? 500 : 400,
                    color: isEnabled ? 'text.primary' : 'text.secondary',
                    userSelect: 'none',
                  }}>
                  {flagName}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      </Popover>
    </Box>
  );
};
